import React, { useState, useEffect } from 'react';
import {
    Award, Target, Eye, Heart, Users, TrendingUp, Globe, Shield,
    CheckCircle, Star, Briefcase, Clock, Handshake, Zap,
    BarChart, Package, Ship, Truck, ArrowRight, ChevronRight, Leaf,
    AirVent,
    Plane
} from 'lucide-react';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { Link } from 'react-router-dom';
import Sabir_Kadivar from '../assets/Sabir Kadivar.jpeg'

const About = () => {
    const [isVisible, setIsVisible] = useState({});
    const [countUp, setCountUp] = useState({});
    const [currentWhyIcon, setCurrentWhyIcon] = useState(0);
    const [isWhyTransitioning, setIsWhyTransitioning] = useState(false);

    useEffect(() => {
            const interval = setInterval(() => {
                setIsWhyTransitioning(true);
                setTimeout(() => {
                    setCurrentWhyIcon(prev => (prev + 1) % whySlides.length);
                    setIsWhyTransitioning(false);
                }, 400);
            }, 3500);
            return () => clearInterval(interval);
        }, []);
    

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
                    }
                });
            },
            { threshold: 0.1 }
        );

        const elements = document.querySelectorAll('.animate-on-scroll');
        elements.forEach((el) => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    // Counter Animation
    useEffect(() => {
        const counters = [
            { id: 'clients', target: 500 },
            { id: 'countries', target: 10 },
            { id: 'shipments', target: 1986 },
            { id: 'experience', target: 25 }
        ];

        counters.forEach(({ id, target }) => {
            if (isVisible[`counter-section`]) {
                let current = 0;
                const increment = target / 50;
                const timer = setInterval(() => {
                    current += increment;
                    if (current >= target) {
                        setCountUp((prev) => ({ ...prev, [id]: target }));
                        clearInterval(timer);
                    } else {
                        setCountUp((prev) => ({ ...prev, [id]: Math.floor(current) }));
                    }
                }, 30);
            }
        });
    }, [isVisible]);

    const values = [
        {
            icon: <Shield className="w-12 h-12" />,
            title: "Trust & Integrity",
            description: "Building lasting relationships through honest and transparent business practices.",
            color: "from-green-500 to-emerald-700"
        },
        {
            icon: <Zap className="w-12 h-12" />,
            title: "Innovation",
            description: "Embracing smart technology and modern strategies to improve efficiency in global trade.",
            color: "from-emerald-500 to-teal-700"
        },
        {
            icon: <Users className="w-12 h-12" />,
            title: "Customer First",
            description: "Your success is our priority. We go above and beyond for our clients.",
            color: "from-teal-500 to-cyan-700"
        },
        {
            icon: <Award className="w-12 h-12" />,
            title: "Excellence",
            description: "Delivering exceptional quality in every shipment and service we provide.",
            color: "from-green-600 to-green-800"
        }
    ];

    const milestones = [
        {
            year: "2000",
            title: "Company Founded",
            description: "Started our journey with a vision to transform international trade"
        },
        {
            year: "2012",
            title: "Regional Expansion",
            description: "Extended services to 15+ states across India"
        },
        {
            year: "2019",
            title: "IEC Registration",
            description: "Obtained Import Export Code (IEC), enabling seamless international trade operations."
        },
        {
            year: "2020",
            title: "APEDA Certified",
            description: "Registered with APEDA, strengthening our credibility and compliance in agricultural export operations."
        },
        {
            year: "2024",
            title: "Nationwide Presence",
            description: "Expanded across 15+ states with 500+ trusted partners."
        },
    ];

    const team = [
        {
            name: "Yakub Kadivar",
            position: "Founder & CEO",
            experience: "25+ Years",
            specialty: "International Trade"
        },
        {
            name: "Sabir Kadivar",
            position: "Co-Founder & Operations Director",
            experience: "7+ Years",
            specialty: "Operations Management",
            image: Sabir_Kadivar // Replace with actual image path
        },
        {
            name: "Hanif Kadivar",
            position: "Products & Marketing Head",
            experience: "25+ Years",
            specialty: "Market Expansion"
        },
    ];

    const achievements = [
        // { icon: <Award />, text: "Top Export Company 2023" },
        { icon: <Star />, text: "5-Star Customer Rating" },
        // { icon: <Globe />, text: "ISO 9001:2015 Certified" },
        { icon: <Handshake />, text: "500+ Business Partners" },
        { icon: <TrendingUp />, text: "40% YoY Growth" },
        { icon: <Shield />, text: "Zero Compliance Issues" }
    ];

    const services = [
        { icon: <Ship />, label: "Sea Freight" },
        { icon: <Truck />, label: "Land Transport" },
        { icon: <Plane />, label: "Air Cargo" },
    ];

    // Why Choose slider data
    const whySlides = [
        {
            icon: <Plane className="w-20 h-20" />,
            color: "from-green-700 to-emerald-800",
        },
        {
            icon: <Ship className="w-20 h-20" />,
            color: "from-emerald-600 to-green-800",
        },
        {
            icon: <Truck className="w-20 h-20" />,
            color: "from-teal-600 to-green-700",
        },
    ];

    return (
        <>
            <Navbar />
            <div className="min-h-screen bg-white">

                {/* Hero Section */}
                <section className="relative bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white overflow-hidden">
                    <div className="absolute inset-0 bg-black opacity-30"></div>

                    {/* Animated Background Elements */}
                    <div className="absolute inset-0">
                        <div className="absolute top-20 left-10 w-96 h-96 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
                        <div className="absolute top-40 right-10 w-96 h-96 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
                        <div className="absolute bottom-20 left-1/2 w-96 h-96 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
                        <div className="text-center">
                            <div className="animate-fade-in-down">
                                <h1 className="text-5xl md:text-7xl font-bold mb-6">
                                    About <span className="bg-gradient-to-r from-green-300 to-teal-300 bg-clip-text text-transparent">Nurani Enterprise</span>
                                </h1>
                                <div className="w-32 h-1 bg-gradient-to-r from-green-400 to-teal-400 mx-auto mb-8 rounded-full"></div>
                            </div>

                            <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto leading-relaxed mb-12 animate-fade-in-up">
                                Pioneering International Trade Solutions for Over 25 Years. Your Trusted Partner in Connecting Businesses Across Continents.
                            </p>

                            <div className="flex flex-wrap justify-center gap-4 animate-fade-in">
                                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-8 py-4 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                                    <div className="text-3xl font-bold">25+</div>
                                    <div className="text-sm text-gray-300">Years Experience</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-8 py-4 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                                    <div className="text-3xl font-bold">10+</div>
                                    <div className="text-sm text-gray-300">Countries</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-8 py-4 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                                    <div className="text-3xl font-bold">500+</div>
                                    <div className="text-sm text-gray-300">Happy Clients</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0">
                        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white" />
                        </svg>
                    </div>
                </section>

                {/* Our Story Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid md:grid-cols-2 gap-16 items-center">
                            <div
                                id="story-content"
                                className="animate-on-scroll"
                                style={{
                                    opacity: isVisible['story-content'] ? 1 : 0,
                                    transform: isVisible['story-content'] ? 'translateX(0)' : 'translateX(-50px)',
                                    transition: 'all 0.8s ease-out'
                                }}
                            >
                                <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                    Our Story
                                </div>
                                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                                    Building Bridges Across <span className="text-green-600">Global Markets</span>
                                </h2>
                                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                                    At Nurani Enterprise, we don’t just facilitate trade — we create meaningful connections between businesses across continents. Since our inception, our goal has been to simplify global commerce by removing barriers, reducing risks, and ensuring seamless cross-border transactions.
                                </p>

                                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                                    From sourcing premium agricultural commodities to managing complex logistics and compliance processes, we act as a strategic bridge between suppliers and international buyers. Our expertise ensures that every shipment moves efficiently, securely, and on time.
                                </p>

                                <p className="text-lg text-gray-600 leading-relaxed mb-8">
                                    With a strong global network, transparent operations, and a commitment to long-term partnerships, we empower businesses to expand confidently into new markets. For us, global trade is not just business — it's about building lasting relationships that drive shared growth and success.
                                </p>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-4 bg-green-50 p-4 rounded-xl transform hover:scale-105 transition-all duration-300">
                                        <div className="bg-green-600 text-white p-3 rounded-full">
                                            <Target className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900">Our Mission</h4>
                                            <p className="text-gray-600">Empowering businesses to thrive in global markets</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 bg-teal-50 p-4 rounded-xl transform hover:scale-105 transition-all duration-300">
                                        <div className="bg-teal-600 text-white p-3 rounded-full">
                                            <Eye className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900">Our Vision</h4>
                                            <p className="text-gray-600">To be the world's most trusted trade solutions partner</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* <div
                                id="story-visual"
                                className="animate-on-scroll"
                                style={{
                                    opacity: isVisible['story-visual'] ? 1 : 0,
                                    transform: isVisible['story-visual'] ? 'translateX(0)' : 'translateX(50px)',
                                    transition: 'all 0.8s ease-out'
                                }}
                            >
                                <div className="relative">
                                    <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-teal-500 rounded-3xl transform rotate-6 animate-pulse-slow"></div>
                                    <div className="relative bg-gradient-to-br from-green-600 to-emerald-700 rounded-3xl shadow-2xl overflow-hidden">
                                        <div className="h-96 flex items-center justify-center relative">
                                            <Globe className="w-64 h-64 text-white opacity-20 animate-spin-very-slow absolute" />
                                            <Ship className="w-40 h-40 text-white animate-float" />
                                        </div>
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                    </div>
                                </div>
                            </div> */}

                            <div
                                id="why-choose-image"
                                className="animate-on-scroll relative"
                                style={{
                                    opacity: isVisible['why-choose-image'] ? 1 : 0,
                                    transform: isVisible['why-choose-image'] ? 'translateX(0)' : 'translateX(50px)',
                                    transition: 'all 0.8s ease-out'
                                }}
                            >
                                {/* ── WHY CHOOSE SLIDER ── */}
                                <div className="relative">
                                    {/* Decorative rotated card behind */}
                                    <div
                                        className={`absolute inset-0 rounded-3xl transform rotate-3 opacity-40 bg-gradient-to-br ${whySlides[currentWhyIcon].color}`}
                                        style={{ transition: 'background 0.5s ease' }}
                                    />

                                    <div
                                        className={`relative rounded-3xl overflow-hidden shadow-2xl border-2 border-white bg-gradient-to-br ${whySlides[currentWhyIcon].color} flex items-center justify-center`}
                                        style={{ minHeight: '24rem', transition: 'background 0.5s ease' }}
                                    >
                                        {/* Background texture */}
                                        <div className="absolute inset-0 opacity-10"
                                            style={{
                                                backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.15) 0px, rgba(255,255,255,0.15) 1px, transparent 0px, transparent 50%)',
                                                backgroundSize: '12px 12px',
                                            }}
                                        />

                                        {/* Icon only */}
                                        <div
                                            style={{
                                                opacity: isWhyTransitioning ? 0 : 1,
                                                transform: isWhyTransitioning ? 'scale(0.7)' : 'scale(1)',
                                                transition: 'opacity 0.4s ease, transform 0.4s ease',
                                            }}
                                            className="relative z-10 text-white"
                                        >
                                            {React.cloneElement(whySlides[currentWhyIcon].icon, { className: 'w-48 h-48 text-white opacity-90' })}
                                        </div>
                                    </div>

                                    {/* Dot indicators */}
                                    <div className="flex justify-center gap-2 mt-4">
                                        {whySlides.map((_, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setCurrentWhyIcon(i)}
                                                className="rounded-full transition-all duration-300"
                                                style={{
                                                    width: i === currentWhyIcon ? 28 : 8,
                                                    height: 8,
                                                    background: i === currentWhyIcon ? '#34d399' : 'rgba(0,0,0,0.2)',
                                                }}
                                            />
                                        ))}
                                    </div>

                                    {/* Glow effect */}
                                    <div
                                        className="absolute -inset-2 rounded-3xl -z-10 blur-2xl opacity-25"
                                        style={{
                                            background: `linear-gradient(135deg, #34d399, transparent)`,
                                            transition: 'background 0.5s ease',
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Counter Section */}
                <section
                    id="counter-section"
                    className="animate-on-scroll py-20 bg-gradient-to-r from-green-700 to-teal-700 text-white relative overflow-hidden"
                >
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full filter blur-3xl animate-blob"></div>
                        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                            <div className="text-center transform hover:scale-110 transition-all duration-300">
                                <div className="text-5xl md:text-6xl font-bold mb-2">
                                    {countUp.clients || 0}+
                                </div>
                                <div className="text-xl text-green-100">Happy Clients</div>
                            </div>
                            <div className="text-center transform hover:scale-110 transition-all duration-300">
                                <div className="text-5xl md:text-6xl font-bold mb-2">
                                    {countUp.countries || 0}+
                                </div>
                                <div className="text-xl text-green-100">Countries Served</div>
                            </div>
                            <div className="text-center transform hover:scale-110 transition-all duration-300">
                                <div className="text-5xl md:text-6xl font-bold mb-2">
                                    {(countUp.shipments || 0).toLocaleString()}+
                                </div>
                                <div className="text-xl text-green-100">Successful Shipments</div>
                            </div>
                            <div className="text-center transform hover:scale-110 transition-all duration-300">
                                <div className="text-5xl md:text-6xl font-bold mb-2">
                                    {countUp.experience || 0}+
                                </div>
                                <div className="text-xl text-green-100">Years Experience</div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Our Values Section */}
                <section className="py-20 bg-gray-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            id="values-header"
                            className="animate-on-scroll text-center mb-16"
                            style={{
                                opacity: isVisible['values-header'] ? 1 : 0,
                                transform: isVisible['values-header'] ? 'scale(1)' : 'scale(0.9)',
                                transition: 'all 0.8s ease-out'
                            }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                Our Core <span className="text-green-600">Values</span>
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                The principles that guide everything we do
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {values.map((value, index) => (
                                <div
                                    key={index}
                                    id={`value-${index}`}
                                    className="animate-on-scroll group"
                                    style={{
                                        opacity: isVisible[`value-${index}`] ? 1 : 0,
                                        transform: isVisible[`value-${index}`] ? 'translateY(0)' : 'translateY(30px)',
                                        transition: `all 0.6s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl p-8 h-full transform hover:-translate-y-3 transition-all duration-300 border-t-4 border-green-600">
                                        <div className={`bg-gradient-to-br ${value.color} text-white w-20 h-20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                                            {value.icon}
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{value.title}</h3>
                                        <p className="text-gray-600 leading-relaxed">{value.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Journey Timeline */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            id="timeline-header"
                            className="animate-on-scroll text-center mb-16"
                            style={{
                                opacity: isVisible['timeline-header'] ? 1 : 0,
                                transition: 'all 0.8s ease-out'
                            }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                Our <span className="text-green-600">Journey</span>
                            </h2>
                            <p className="text-xl text-gray-600">Milestones that shaped our success</p>
                        </div>

                        <div className="relative">
                            {/* Timeline Line */}
                            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-green-600 to-teal-600"></div>

                            {milestones.map((milestone, index) => (
                                <div
                                    key={index}
                                    id={`milestone-${index}`}
                                    className="animate-on-scroll mb-12"
                                    style={{
                                        opacity: isVisible[`milestone-${index}`] ? 1 : 0,
                                        transform: isVisible[`milestone-${index}`] ? 'translateY(0)' : 'translateY(30px)',
                                        transition: `all 0.6s ease-out ${index * 0.2}s`
                                    }}
                                >
                                    <div className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                                        <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                                            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 border border-green-100">
                                                <div className="text-3xl font-bold text-green-600 mb-2">{milestone.year}</div>
                                                <h3 className="text-2xl font-bold text-gray-900 mb-3">{milestone.title}</h3>
                                                <p className="text-gray-600 leading-relaxed">{milestone.description}</p>
                                            </div>
                                        </div>

                                        <div className="relative flex items-center justify-center w-16 h-16 flex-shrink-0">
                                            <div className="absolute w-16 h-16 bg-gradient-to-br from-green-600 to-teal-600 rounded-full animate-ping opacity-75"></div>
                                            <div className="relative w-12 h-12 bg-gradient-to-br from-green-600 to-teal-600 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                                                {index + 1}
                                            </div>
                                        </div>

                                        <div className="w-full md:w-5/12"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Leadership Team */}
                <section className="py-20 bg-gradient-to-br from-gray-50 to-green-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            id="team-header"
                            className="animate-on-scroll text-center mb-16"
                            style={{
                                opacity: isVisible['team-header'] ? 1 : 0,
                                transition: 'all 0.8s ease-out'
                            }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                Meet Our <span className="text-green-600">Leadership Team</span>
                            </h2>
                            <p className="text-xl text-gray-600">Experienced professionals driving our success</p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {team.map((member, index) => (
                                <div
                                    key={index}
                                    id={`team-${index}`}
                                    className="animate-on-scroll group"
                                    style={{
                                        opacity: isVisible[`team-${index}`] ? 1 : 0,
                                        transform: isVisible[`team-${index}`] ? 'scale(1)' : 'scale(0.9)',
                                        transition: `all 0.6s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <div className="bg-white rounded-2xl shadow-lg hover:shadow-2xl overflow-hidden transform hover:-translate-y-3 transition-all duration-300">
                                        <div className={`h-64 ${member.image ? '' : 'bg-gradient-to-br from-green-600 to-teal-700'} flex items-center justify-center relative overflow-hidden group-hover:scale-110 transition-all duration-300`}>
                                            {member.image ? (
                                                <img
                                                    src={member.image}
                                                    alt={member.name}
                                                    className="absolute inset-0 w-full h-full object-cover"
                                                />
                                            ) : (
                                                <>
                                                    <Users className="w-32 h-32 text-white opacity-30" />
                                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                                                </>
                                            )}
                                        </div>
                                        <div className="p-6">
                                            <h3 className="text-xl font-bold text-gray-900 mb-1">{member.name}</h3>
                                            <p className="text-green-600 font-semibold mb-3">{member.position}</p>
                                            <div className="space-y-2 text-sm text-gray-600">
                                                <div className="flex items-center gap-2">
                                                    <Clock className="w-4 h-4 text-green-600" />
                                                    <span>{member.experience}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Briefcase className="w-4 h-4 text-green-600" />
                                                    <span>{member.specialty}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Achievements */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div
                            id="achievements-header"
                            className="animate-on-scroll text-center mb-16"
                            style={{
                                opacity: isVisible['achievements-header'] ? 1 : 0,
                                transition: 'all 0.8s ease-out'
                            }}
                        >
                            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                Our <span className="text-green-600">Achievements</span>
                            </h2>
                            <p className="text-xl text-gray-600">Recognition and milestones</p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {achievements.map((achievement, index) => (
                                <div
                                    key={index}
                                    id={`achievement-${index}`}
                                    className="animate-on-scroll"
                                    style={{
                                        opacity: isVisible[`achievement-${index}`] ? 1 : 0,
                                        transform: isVisible[`achievement-${index}`] ? 'translateX(0)' : 'translateX(-20px)',
                                        transition: `all 0.6s ease-out ${index * 0.1}s`
                                    }}
                                >
                                    <div className="flex items-center gap-4 bg-gradient-to-br from-green-50 to-teal-50 p-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-105 transition-all duration-300 border border-green-100">
                                        <div className="bg-gradient-to-br from-green-600 to-teal-600 text-white p-3 rounded-full flex-shrink-0">
                                            {React.cloneElement(achievement.icon, { className: "w-6 h-6" })}
                                        </div>
                                        <p className="font-semibold text-gray-800">{achievement.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Services Preview */}
                <section className="py-20 bg-gradient-to-br from-green-900 via-emerald-900 to-teal-900 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full filter blur-3xl animate-blob"></div>
                        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl animate-blob animation-delay-2000"></div>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <div className="text-center mb-12">
                            <h2 className="text-4xl md:text-5xl font-bold mb-4">
                                Our <span className="text-green-300">Services</span>
                            </h2>
                            <p className="text-xl text-green-100">Comprehensive solutions for your export needs</p>
                        </div>

                        <div className="flex justify-center">
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 max-w-4xl w-full">
                                {services.map((service, index) => (
                                    <div
                                        key={index}
                                        className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center hover:bg-white/20 transform hover:scale-110 hover:-translate-y-2 transition-all duration-300"
                                    >
                                        <div className="text-white mb-3 flex justify-center">
                                            {React.cloneElement(service.icon, { className: "w-12 h-12" })}
                                        </div>
                                        <p className="text-sm font-semibold">{service.label}</p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="text-center mt-12">
                            <Link to="/service-details">
                                <button className="bg-white text-green-700 px-8 py-4 rounded-full font-bold text-lg hover:bg-green-50 transform hover:scale-105 transition-all duration-300 shadow-xl flex items-center gap-2 mx-auto">
                                    Explore All Services
                                    <ArrowRight className="w-5 h-5" />
                                </button>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-20 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-3xl shadow-2xl overflow-hidden">
                            <div className="grid md:grid-cols-2 gap-8 items-center p-12">
                                <div className="text-white">
                                    <h2 className="text-4xl font-bold mb-4">
                                        Ready to Start Your Global Journey?
                                    </h2>
                                    <p className="text-xl text-green-100 mb-6">
                                        Join hundreds of businesses that trust <span className="font-semibold text-white">Nurani Enterprise</span> for their international trade needs.
                                    </p>

                                    <Link to="/contact">
                                        <button className="bg-white text-green-600 px-8 py-4 rounded-full font-bold text-lg hover:bg-green-50 transform hover:scale-105 transition-all duration-300 shadow-xl">
                                            Get Started Today
                                        </button>
                                    </Link>
                                </div>
                                <div className="relative h-64 flex items-center justify-center">
                                    <div className="absolute inset-0 bg-white/10 rounded-full animate-pulse-slow"></div>
                                    <Handshake className="w-48 h-48 text-white/30 animate-float" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <style jsx>{`
        @keyframes fade-in-down {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes blob {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }

        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }

        @keyframes spin-very-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .animate-fade-in-down {
          animation: fade-in-down 1s ease-out;
        }

        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out 0.3s both;
        }

        .animate-fade-in {
          animation: fade-in 1s ease-out 0.6s both;
        }

        .animate-blob {
          animation: blob 7s infinite;
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }

        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .animate-pulse-slow {
          animation: pulse-slow 3s ease-in-out infinite;
        }

        .animate-spin-very-slow {
          animation: spin-very-slow 30s linear infinite;
        }
      `}</style>
            </div>
            <Footer />
        </>
    );
};

export default About;